import { amount, currency, date, time } from 'utils'
import { Client, Order, Plan } from 'models'
import { translations } from 'modules/Orders/translations'
import { ExternalLinkIcon } from '@heroicons/react/solid'
import { Link } from 'react-router-dom'

interface ReviewTableProps {
  order: Partial<Order>
  client: Partial<Client> | undefined
  plan: Partial<Plan> | undefined
  onAssignOwnerRequest?: () => void
}

export function ReviewTable({
  order,
  client,
  plan,
  onAssignOwnerRequest
}: ReviewTableProps) {
  return (
    <dl className="rounded shadow mx-4 my-8 animate-fade-in-down">
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Cliente</dt>
        <dd className="flex mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <Link
            to={`/clients/${client?.id}/general`}
            className="text-gray-900 hover:underline"
          >
            <div className="flex">
              {client?.account_name}
              <ExternalLinkIcon
                className="h-5 w-5 text-gray-900 ml-2"
                aria-hidden="true"
              />
            </div>
          </Link>
        </dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Acesso</dt>
        <dd className="flex mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          <Link
            to={`/access-plans/${plan?.id}/view/general`}
            className="text-gray-900 hover:underline"
          >
            <div className="flex">
              {plan?.name}
              <ExternalLinkIcon
                className="h-5 w-5 text-gray-9000 ml-2"
                aria-hidden="true"
              />
            </div>
          </Link>
        </dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Valor sugerido</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order.amount
            ? `${currency(
                order.relationships?.plan.attributes.currency
              )} ${amount(
                order.relationships?.plan.attributes.amount as number
              )}`
            : '-'}
        </dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">
          Valor comercializado
        </dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order.amount
            ? `${currency(
                order.relationships?.plan.attributes.currency
              )} ${amount(order.amount as number)}`
            : '-'}
        </dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Taxa de adesão</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order.setup_fee
            ? `${currency(
                order.relationships?.plan.attributes.currency
              )} ${amount(order.setup_fee)}`
            : '-'}
        </dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">
          Valor mínimo da fatura
        </dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order?.minimum_invoice_amount && order.minimum_invoice_amount !== 0
            ? `${currency(plan?.currency)} ${amount(
                order.minimum_invoice_amount
              )}`
            : '-'}
        </dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Multa</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order?.fine_mode && order.fine_amount
            ? order.fine_mode === 'fixed'
              ? `${currency(plan?.currency)} ${amount(order?.fine_amount)}`
              : `${amount(order?.fine_amount)} %`
            : '-'}
        </dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Data do pedido</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {`${date(order.ordered_at)} ${time(order.ordered_at)}`}
        </dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Data de criação</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{`${date(
          order.created_at
        )} ${time(order.created_at)}`}</dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Data de ativação</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{`${date(
          order.activated_at
        )} ${time(order.activated_at)}`}</dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Data de término</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{`${date(
          order.end_at
        )} ${time(order.end_at)}`}</dd>
      </div>
      <div className="bg-white p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Cupom</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order.relationships?.coupon
            ? `Aplicação ${
                translations.duration_mode[
                  order.relationships.coupon.attributes.duration_mode
                ] as string
              }: ${
                order.relationships.coupon.attributes.duration_mode ===
                'repeating'
                  ? `${order.relationships.coupon.attributes.duration} ${
                      translations.coupon_interval[
                        order.relationships.coupon.attributes.interval
                      ]
                    }`
                  : ''
              }` +
              ` ${
                order.relationships.coupon.attributes.mode === 'fixed'
                  ? `${currency(
                      order.relationships.coupon.attributes.currency
                    )} ${amount(order.relationships.coupon.attributes.amount)}`
                  : `${amount(order.relationships.coupon.attributes.amount)}%`
              }`
            : '-'}
        </dd>
      </div>
      <div className="bg-gray-50 p-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
        <dt className="text-sm font-medium text-gray-500">Responsável</dt>
        <dd className="flex items-center mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
          {order.relationships?.staff_member?.attributes?.fullname ??
            'Não atribuído'}
          {onAssignOwnerRequest && (
            <button
              type="button"
              onClick={onAssignOwnerRequest}
              className="ml-3 text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
            >
              {order.relationships?.staff_member ? 'Alterar' : 'Atribuir'}
            </button>
          )}
        </dd>
      </div>
    </dl>
  )
}
